import { gql } from '@apollo/client'
import { useCleanFilterForRequest, useFilterValue, useSearch, useSort } from '@btime/react-contexts'
import React from 'react'

import { placesClient } from '../../../../utils/apollo/api'
import { usePaginatedQuery } from '../../../../utils/apollo/_helpers/pagination'
import { usePlaceType } from '../../_context/PlaceTypeContext'
import LazyAction from '../../../../components/helpers/LazyAction'
import StatePresenter from '../../../../components/helpers/StatePresenter'
import { cleanWhenEnabledFilterIsSet } from '../../../_helpers/filterByEnabled'

const Context = React.createContext()
const { Provider } = Context
export const usePlacesState = () => React.useContext(Context) || {}
export const usePlaces = () => usePlacesState()?.places || []

const QUERY = gql`
  query Places($page: Int, $search: String, $sort: PlaceSort, $filter: PlaceFilter) {
    places(page: $page, search: $search, sort: $sort, filter: $filter) {
      id
      name
      placeLevelsResume
      insertedAt
      enabled
      type
    }
  }
`

const groupPlaceLevelIdsByLevel = rawPlaceLevelIds => {
  if (!rawPlaceLevelIds?.length) return undefined

  const grouped = rawPlaceLevelIds.reduce((acc, item) => {
    const key = item.levelId
    if (!acc[key]) acc[key] = []
    acc[key].push(item.id)
    return acc
  }, {})

  return Object.values(grouped)
}

const PlacesLoader = ({ children, uncontrolledChildren }) => {
  const { field, type } = useSort()
  const search = useSearch()
  const filter = useCleanFilterForRequest()
  const rawPlaceLevelIds = useFilterValue('placeLevelIds', [])
  const placeType = usePlaceType()

  const groupedPlaceLevelIds = groupPlaceLevelIdsByLevel(rawPlaceLevelIds)
  const placeLevelIdsFilter = groupedPlaceLevelIds ? { placeLevelIdsGroups: groupedPlaceLevelIds } : {}

  const { data, fetchMore, ...state } = usePaginatedQuery(QUERY, {
    variables: {
      page: 1,
      search,
      sort: { field: field || 'NAME', type },
      filter: {
        ...cleanWhenEnabledFilterIsSet({ ...filter, includeDisabled: true, types: [placeType] }),
        placeLevelIds: undefined,
        ...placeLevelIdsFilter
      }
    },
    client: placesClient
  })

  return (
    <Provider value={data}>
      <StatePresenter {...state} uncontrolledChildren={uncontrolledChildren}>
        {children}
        <LazyAction action={fetchMore} waiting={state.loading} />
      </StatePresenter>
    </Provider>
  )
}

export default PlacesLoader
